/** @file applyEntryResize: lands a size / pivot change: one facet.setEntry, or the fit prompt and a refit. */
import type { Command } from '@zilo/pose3d-commands/types/command';
import { slotKey } from '@zilo/pose3d-format/derive/slotKey';
import type { Image } from '@zilo/pose3d-image-io/types/image';
import type { EditorStore } from '../../../state/editorTypes';
import type { ArtDeps, FitMode, MisfitImage } from '../artTypes';
import { thumbnailOf } from '../thumbnailOf';
import type { EntryResize, EntryResizeFields } from './entryResizeFields';
import { refitEntry } from './refitEntry';

/** The store, the upload call (null: no server) and the Pad / Scale / Cancel prompt (null = cancel). */
export type ResizeEnv = { store: EditorStore; deps: Pick<ArtDeps, 'uploadImage'> | null; askFit: (misfits: MisfitImage[]) => Promise<FitMode | null> };

/** The filled tiles of the entry in the document, ascending. */
function filledTiles(store: EditorStore, r: EntryResize): number[] {
  const d = store.getState().puppet.doc?.puppet.drawables.find((x) => x.id === r.drawable);
  const e = d?.kind === 'impostor' || d?.kind === 'effect' ? d.facets?.[r.entry] : undefined;
  return Object.keys(e?.slots ?? {}).map(Number).sort((a, b) => a - b);
}

/** Sends the one `facet.setEntry` ("Resize <drawable>" / "Move pivot of <drawable>"); true when it landed. */
async function sendEntry(store: EditorStore, r: EntryResize, fields: EntryResizeFields): Promise<boolean> {
  const label = fields.size === undefined ? `Move pivot of ${r.drawable}` : `Resize ${r.drawable}`;
  const command: Command = { name: 'facet.setEntry', input: { drawable: r.drawable, entry: r.entry, fields } };
  try {
    const replies = await store.getState().dispatch([command], label);
    return replies.every((x) => x.ok);
  } catch {
    return false;
  }
}

/** The old pixels of every filled tile of the entry from the preview bundle; null when one is missing there. */
function tilePixels(store: EditorStore, r: EntryResize, tiles: readonly number[]): { tile: number; pixels: Image }[] | null {
  const bundle = store.getState().preview.bundle;
  const out = tiles.map((tile) => ({ tile, pixels: thumbnailOf(bundle, { drawable: r.drawable, entry: r.entry, tile }) }));
  return out.every((t) => t.pixels !== null) ? (out as { tile: number; pixels: Image }[]) : null;
}

/** A size change on an entry holding images: Pad / Scale / Cancel, then one refit batch (`refitEntry`). */
async function refit(env: ResizeEnv, r: EntryResize, fields: EntryResizeFields, tiles: number[]): Promise<boolean> {
  const s = env.store.getState();
  const pixels = tilePixels(env.store, r, tiles);
  if (pixels === null || env.deps === null) {
    s.notify('error', env.deps === null ? `Resize ${r.drawable}: no server to upload the resized tiles to` : `Resize ${r.drawable}: the preview has no pixels for every tile yet`);
    return false;
  }
  const size: [number, number] = [r.size[0], r.size[1]];
  // the prompt reads names and sizes only; the pixels come from the preview bundle
  const misfits = tiles.map((tile) => ({ name: slotKey(r.drawable, r.entry, tile), slot: slotKey(r.drawable, r.entry, tile), png: new Uint8Array(0), size: [r.size0[0], r.size0[1]] as [number, number], slotSize: size }));
  const fit = await env.askFit(misfits);
  if (fit === null) return false;
  const offset: [number, number] = [r.pivot[0] - r.pivot0[0], r.pivot[1] - r.pivot0[1]];
  return refitEntry(env.store, env.deps, {
    drawable: r.drawable, entry: r.entry, size, ...(fields.pivotFrac === undefined ? {} : { pivotFrac: fields.pivotFrac }), tiles: pixels, offset, fit,
  });
}

/**
 * The path a quad handle drag and the inspector's Rig fields share: `fields` (from `entryResizeFields`) as one
 * `facet.setEntry`, or, when the size changed on an entry that holds images, the fit prompt and the refit batch
 * (Cancel sends nothing). True when the change landed.
 */
export function applyEntryResize(env: ResizeEnv, r: EntryResize, fields: EntryResizeFields): Promise<boolean> {
  const tiles = filledTiles(env.store, r);
  return fields.size !== undefined && tiles.length > 0 ? refit(env, r, fields, tiles) : sendEntry(env.store, r, fields);
}
